<template>
  <van-popup
    :show="modelValue"
    position="bottom"
    round
    safe-area-inset-bottom
    :close-on-click-overlay="!submitting"
    @update:show="handlePopupUpdate"
  >
    <section class="article-report-sheet">
      <h2 class="article-report-sheet__title">{{ t('article.reportTitle') }}</h2>

      <button
        v-for="item in reasons"
        :key="item.value"
        class="article-report-sheet__item"
        type="button"
        :disabled="submitting"
        @click="submitReport(item.value)"
      >
        {{ item.label }}
      </button>

      <button
        class="article-report-sheet__cancel"
        type="button"
        :disabled="submitting"
        @click="close"
      >
        {{ t('common.cancel') }}
      </button>
    </section>
  </van-popup>
</template>

<script setup>
import { computed, ref } from 'vue';
import { closeToast, showLoadingToast } from 'vant';
import { useI18n } from 'vue-i18n';
import { useProfileStore } from '@/stores/profile';
import { articleComplaint } from '@/api/article';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  articleId: {
    type: [Number, String],
    required: true
  },
  submitter: {
    type: Function,
    default: null
  }
});

const emit = defineEmits(['update:modelValue', 'success', 'fail']);
const { t } = useI18n();
const profileStore = useProfileStore();
const submitting = ref(false);

const reasons = computed(() => [
  { label: t('article.reasonAttack'), value: '攻击引战' },
  { label: t('article.reasonPorn'), value: '色情低俗' },
  { label: t('article.reasonDiscomfort'), value: '引人不适' },
  { label: t('article.reasonSpam'), value: '垃圾广告' }
]);

function close() {
  if (!submitting.value) {
    emit('update:modelValue', false);
  }
}

function handlePopupUpdate(value) {
  if (!submitting.value) {
    emit('update:modelValue', value);
  }
}

async function submitReport(reason) {
  if (submitting.value) return;

  submitting.value = true;
  showLoadingToast({
    message: t('common.submitting'),
    forbidClick: true,
    duration: 0
  });

  const payload = {
    address: profileStore.currentAddress || null,
    articleId: Number(props.articleId),
    reason,
    content: ''
  };

  try {
    const submit = props.submitter || articleComplaint;
    await submit(payload);
    closeToast();
    emit('update:modelValue', false);
    emit('success', payload);
  } catch (error) {
    closeToast();
    emit('fail', error);
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
.article-report-sheet {
  overflow: hidden;
  padding: 16px 16px 0;
  background: var(--wp-color-white);
}

.article-report-sheet__title {
  margin: 0 0 10px;
  color: var(--wp-color-text);
  font-size: 18px;
  font-weight: 500;
  line-height: 25px;
  text-align: center;
}

.article-report-sheet__item,
.article-report-sheet__cancel {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 45px;
  padding: 12px;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: var(--wp-color-secondary);
  font-size: 14px;
  line-height: 20px;
}

.article-report-sheet__item {
  border-bottom: 1px solid var(--wp-color-line);
}

.article-report-sheet__cancel {
  min-height: 54px;
  margin-top: 0;
  border-top: 10px solid var(--wp-color-line-light);
  color: #000000;
}

.article-report-sheet__item:disabled,
.article-report-sheet__cancel:disabled {
  opacity: 0.6;
}
</style>
